import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig } from '@inavista/components/grid';

// Shipment order popup → Receipts → Exceptions on @inavista/components. Same columns, labels, order, widths, alignment,
// links (Item, Description), Return checkbox, Delete command and toolbar (Columns, Add Exception, Search, Clear filters)
// as the Syncfusion grid it replaces. Rows are the popup's own exceptions (LocalRowsGridApi), saved with the popup.
// Each editable cell shows its editor (CellEditorHost), so the exception / item rules inside
// them run as before.

export const SHIPPED_EXCEPTIONS_URL = 'page://shipped-exceptions';
export const SHIPPED_EXCEPTIONS_PERSISTENCE_KEY = 'rims.service.shipment-order-receipts-exceptions';
export const ADD_EXCEPTION_ACTION = 'add-exception';
export const DELETE_EXCEPTION_ACTION = 'Delete Item';
/** A hidden field the page writes when a row opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const SHIPPED_EXCEPTIONS_OPEN_FIELD = '__open';

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

export const SHIPPED_EXCEPTIONS_COLUMNS: [string, string][] = [
  ['tempRowId', 'int'],
  ['reasonDescription', 'nvarchar'],
  ['itemCode', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['quantity', 'decimal'],
  ['type', 'nvarchar'],
  ['isReturn', 'bit'],
  [SHIPPED_EXCEPTIONS_OPEN_FIELD, 'nvarchar'],
];

/** The columns with a cell editor (CellEditorHost), in the old column order. */
export const SHIPPED_EXCEPTIONS_EDIT_FIELDS = ['reasonDescription', 'itemCode', 'itemDescription', 'quantity', 'type', 'isReturn'];

const SHOWN = SHIPPED_EXCEPTIONS_EDIT_FIELDS;

export interface ShippedExceptionsGridContext {
  editors: Record<string, TemplateRef<unknown>>;
  returnCell: TemplateRef<unknown>;
  canAdd: boolean;
  canEdit: () => boolean;
  deleteLockReason: () => string | null;
}

export function buildShippedExceptionsActionIcons(ctx: ShippedExceptionsGridContext): ActionIconDef[] {
  // The old command: greyed and not clickable unless the order is Delivery Acknowledged.
  return [{
    key: DELETE_EXCEPTION_ACTION, label: 'Delete Item', icon: TRASH_ICON,
    colorFn: () => ctx.deleteLockReason() ? INACTIVE : BLACK,
    labelFn: () => ctx.deleteLockReason() ?? 'Delete Item',
  }];
}

export function buildShippedExceptionsGridConfig(ctx: ShippedExceptionsGridContext): GridConfig {
  return {
    apiBaseUrl: SHIPPED_EXCEPTIONS_URL,
    clientSide: true,
    keyColumn: 'tempRowId',
    rowHeight: 30,
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: ['tempRowId', SHIPPED_EXCEPTIONS_OPEN_FIELD],
    // Exception, Item, Description and Return were out of the column chooser.
    alwaysVisibleColumns: ['reasonDescription', 'itemCode', 'itemDescription', 'isReturn'],
    columnLabels: {
      reasonDescription: 'Exception', itemCode: 'Item', itemDescription: 'Description', quantity: 'Quantity', type: 'Type',
      isReturn: 'Return',
    },
    // The old alignment: Quantity right, the rest left; headers left (headerTextAlign 'left').
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, {
      horizontal: c === 'quantity' ? 'right' as const : 'left' as const, header: { horizontal: 'left' as const },
    }])),
    // The old widths; Type had none and took the space left, so it is the only one that stretches.
    columnWidths: { reasonDescription: 150, itemCode: 175, itemDescription: 195, quantity: 150, type: 150, isReturn: 80 },
    stretchColumns: true,
    columnSizing: Object.fromEntries(SHOWN.filter(c => c !== 'type').map(c => [c, { noStretch: true }])),
    columnTypes: { isReturn: 'boolean' },
    hyperlinkColumns: ['itemCode', 'itemDescription'],
    // Exception and Item had the Excel (checkbox list) filter; Return had none.
    excelFilterColumns: ['reasonDescription', 'itemCode'],
    cellTemplates: { isReturn: ctx.returnCell },
    // The old Delete column was 110 wide.
    actionsColumnMinWidth: 110,
    // Rows open only for a user who may change them, as the old editSettings.allowEditing (the page rebuilds
    // the config when that changes): a row whose every cell is locked would still open, with nothing to edit.
    editMode: ctx.canEdit() ? 'inline' : 'none',
    editSaveMode: 'row',
    // A click on a cell opens its row, as the old grid's mouseup handler did.
    editTrigger: 'click',
    editableColumns: SHIPPED_EXCEPTIONS_EDIT_FIELDS,
    editorTemplates: ctx.editors,
    // Exceptions are edited while the order is Delivery Acknowledged, with Inv_Order_Receipt_Popup_Receipt_Tab edit.
    readOnlyCellFn: () => !ctx.canEdit(),
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions: ctx.canAdd ? [{
      key: ADD_EXCEPTION_ACTION, label: 'Add Exception', kind: 'button', icon: PLUS_ICON,
      disabled: () => !ctx.canEdit(),
      disabledReason: () => ctx.canEdit() ? '' : 'Exceptions can be added while the order is Delivery Acknowledged.',
    }] : [],
    // The old toolbar: Columns, Add Exception (PO_Popup_ItemTabGrid_Toolbar_Add_Item), Search, Clear filters. No Excel
    // export: its button was added to the wrong list and never showed.
    enableColumnsPicker: true,
    enableExport: false,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: SHIPPED_EXCEPTIONS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
